import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {datadogRum} from '@datadog/browser-rum';

import useHelpCenter from '@/hooks/useHelpCenter';
import useTracking from '@/hooks/useTracking';
import {searchArticles} from '@/utils/helpCenter/searchArticles';
import {
  type HelpCenterArticle,
  type HelpCenterCollection,
  type HelpCenterPayload,
} from '@/utils/helpCenter/types';

const SEARCH_DEBOUNCE_MS = 250;

// Single-character prefix matching returns most of the corpus, which reads as noise, not search.
const MIN_SEARCH_CHARS = 2;

type HelpPanelSession = {
  data: HelpCenterPayload;
  loading: boolean;
  error: boolean;
  query: string;
  setQuery: (query: string) => void;
  debouncedQuery: string;
  clearSearch: () => void;
  isSearching: boolean;
  results: HelpCenterArticle[];
  selected: HelpCenterCollection | null;
  setSelected: (collection: HelpCenterCollection | null) => void;
};

const HelpPanelSessionContext = createContext<HelpPanelSession | null>(null);

/**
 * The split and tabbed layouts mount HelpPanel at different tree positions, so crossing the
 * breakpoint remounts it. Holding the fetch and the search above that boundary is what lets the
 * modal switch layouts mid-session without dropping the customer's query or refetching.
 */
export const HelpPanelSessionProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {data, loading, error, failure} = useHelpCenter();
  const {track} = useTracking();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selected, setSelected] = useState<HelpCenterCollection | null>(null);
  const hasTrackedLoadFailure = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (loading || !failure || hasTrackedLoadFailure.current) return;
    hasTrackedLoadFailure.current = true;
    datadogRum.addAction('help-center.content-load-failed', {
      status: failure.status ?? null,
      reason: failure.reason,
    });
    track('help-center-content-load-failed', {
      page: window.location.pathname,
      misc_json: JSON.stringify({status: failure.status ?? null, reason: failure.reason}),
    });
  }, [loading, failure, track]);

  const isSearching = debouncedQuery.length >= MIN_SEARCH_CHARS;

  const results = useMemo(
    () => (isSearching ? searchArticles(data.articles, debouncedQuery) : []),
    [isSearching, data.articles, debouncedQuery],
  );

  useEffect(() => {
    if (!isSearching || loading || error) return;

    track('searched-help-panel', {
      page: window.location.pathname,
      misc_json: JSON.stringify({
        query_length: debouncedQuery.length,
        result_count: results.length,
        zero_results: results.length === 0,
      }),
    });
  }, [debouncedQuery, error, isSearching, loading, results.length, track]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      query,
      setQuery,
      debouncedQuery,
      clearSearch,
      isSearching,
      results,
      selected,
      setSelected,
    }),
    [data, loading, error, query, debouncedQuery, clearSearch, isSearching, results, selected],
  );

  return (
    <HelpPanelSessionContext.Provider value={value}>{children}</HelpPanelSessionContext.Provider>
  );
};

export const useHelpPanelSession = (): HelpPanelSession => {
  const session = useContext(HelpPanelSessionContext);
  if (!session) {
    throw new Error('useHelpPanelSession must be used within a HelpPanelSessionProvider');
  }
  return session;
};
